import { ReactElement } from 'react';
import {
  useTheme,
  TableContainer,
  TableHead,
  TableRow,
  TableCell as MuiTableCell,
  TableBody,
  Tooltip as MuiTooltip,
  TooltipProps,
  tooltipClasses,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { styled } from '@mui/material/styles';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { SupportResult, SupportWidth } from 'types/domainTypes';
import { ResultsPanelAccordion } from 'components/ResultsPanelAccordion/ResultsPanelAccordion';
import { ResultsPanelTable } from './CalculationResultsPanel';

interface Props {
  results: SupportResult[];
  expanded: boolean;
  onClick: () => void;
  supportWidth: SupportWidth;
}

export const TableCell = styled(MuiTableCell)(() => ({
  width: '33%',
  textAlign: 'left',
  verticalAlign: 'middle',
}));

const WarningIcon = styled(WarningAmberRoundedIcon)(({ theme }) => ({
  width: '32px',
  height: '32px',
  color: theme.palette.primary.main,
  marginRight: 0,
  float: 'right',
  ':hover': { cursor: 'pointer' },
}));

const Box = styled('span')(() => ({
  verticalAlign: 'middle',
  display: 'inline-block',
  width: '50%',
}));

const Tooltip = styled(({ className, ...props }: TooltipProps) => <MuiTooltip {...props} classes={{ popper: className }} />)({
  [`& .${tooltipClasses.tooltip}`]: {
    width: 190,
    padding: '10px',
    textAlign: 'center',
    borderRadius: '7px',
    backgroundColor: '#736f66',
    fontFamily: 'KingspanBrownTT-Light',
  },
});

export function MinimumWidthView({ results, expanded, onClick, supportWidth }: Props): ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <ResultsPanelAccordion
      header={t('calculation-page.results-panel.minimum-width.header')}
      greyOutHeader={t('calculation-page.results-panel.minimum-width.header-per-support')}
      expanded={expanded}
      onClick={onClick}
      data-testid="minimum-width-accordion"
    >
      <TableContainer>
        <ResultsPanelTable>
          <TableHead>
            <TableRow sx={{ '& th': { color: theme.palette.grey[500] } }}>
              <TableCell>{t('calculation-page.results-panel.minimum-width.label-supports')}</TableCell>
              <TableCell>{t('calculation-page.results-panel.minimum-width.label-standard-support-width')}</TableCell>
              <TableCell>{t('calculation-page.results-panel.minimum-width.label-required-width')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.map(({ supportNumber, minWidth }) => {
              const isEndSupport = supportNumber === 1 || supportNumber === results.length;
              const standardWidth = isEndSupport ? supportWidth.end : supportWidth.intermediate;
              const showWarningIcon = minWidth > standardWidth;
              const requiredWidth = showWarningIcon ? minWidth : standardWidth;

              return (
                <TableRow key={supportNumber} style={{ height: '50px' }}>
                  <TableCell component="th" scope="row" data-testid={`support-${supportNumber}`}>
                    {`${t('calculation-page.results-panel.minimum-width.label-support')} ${supportNumber}`}
                  </TableCell>
                  <TableCell component="th" scope="row" data-testid={`standard-width-${supportNumber}`}>
                    {standardWidth}
                  </TableCell>
                  <TableCell align="left" data-testid={`required-width-${supportNumber}`}>
                    <Box>{requiredWidth.toLocaleString(i18n.language, { minimumFractionDigits: 0 })}</Box>
                    <Box>
                      {showWarningIcon && (
                        <Tooltip title={t('calculation-page.results-panel.minimum-width.warning-caption') as string} placement="left">
                          <WarningIcon />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </ResultsPanelTable>
      </TableContainer>
    </ResultsPanelAccordion>
  );
}
